export interface SharedListMutationStore {
  load(): Promise<unknown[]>;
  put(record: { id: string; [key: string]: unknown }): Promise<void>;
  remove(id: string): Promise<void>;
}

const storeName = "operations";

/** Durable storage only; the mutation module owns record validation and replay. */
export class IndexedDBSharedListMutationStore implements SharedListMutationStore {
  constructor(private readonly dbName = "pantryverse-offline-queue") {}

  load(): Promise<unknown[]> {
    return this.transact("readonly", (store) => store.getAll());
  }

  async put(record: { id: string; [key: string]: unknown }): Promise<void> {
    await this.transact("readwrite", (store) => store.put(record));
  }

  async remove(id: string): Promise<void> {
    await this.transact("readwrite", (store) => store.delete(id));
  }

  private open(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      if (typeof indexedDB === "undefined") {
        reject(new Error("Shared list mutation storage is unavailable"));
        return;
      }

      const request = indexedDB.open(this.dbName, 1);
      let blocked = false;
      request.onblocked = () => {
        blocked = true;
        reject(new Error("Shared list mutation storage is blocked"));
      };
      request.onerror = () => {
        reject(request.error ?? new Error("Cannot open mutation storage"));
      };
      request.onupgradeneeded = () => {
        const database = request.result;
        if (!database.objectStoreNames.contains(storeName)) {
          const store = database.createObjectStore(storeName, {
            keyPath: "id",
          });
          // Keep the existing version-one queue schema and its accepted records.
          store.createIndex("listId", "listId", { unique: false });
          store.createIndex("timestamp", "timestamp", { unique: false });
          store.createIndex("type", "type", { unique: false });
        }
      };
      request.onsuccess = () => {
        if (blocked) {
          request.result.close();
          return;
        }
        resolve(request.result);
      };
    });
  }

  private async transact<T>(
    mode: IDBTransactionMode,
    operation: (store: IDBObjectStore) => IDBRequest<T>,
  ): Promise<T> {
    const database = await this.open();
    return new Promise((resolve, reject) => {
      let transaction: IDBTransaction | undefined;
      try {
        transaction = database.transaction(storeName, mode);
        transaction.onabort = () => {
          database.close();
          reject(transaction?.error ?? new Error("Mutation storage aborted"));
        };
        transaction.onerror = () => {
          database.close();
          reject(transaction?.error ?? new Error("Mutation storage failed"));
        };
        const request = operation(transaction.objectStore(storeName));
        // Request success only means the write was staged. A later abort must
        // still reject acceptance, including deletes and settlement records.
        transaction.oncomplete = () => {
          database.close();
          resolve(request.result);
        };
      } catch (error) {
        transaction?.abort();
        database.close();
        reject(error);
      }
    });
  }
}
